import { STORE_KEY_ID_LIST, storeGet } from 'mailup-react-helpers'

import {
  beRecipientApiAddGroup,
  beRecipientApiCheckIfGroupNameExists,
  beRecipientApiCopyGroupToLists,
  beRecipientApiDeleteGroups,
  beRecipientApiDeleteGroupsRecipients,
  beRecipientApiEmptyGroups,
  beRecipientApiGetEntitiesUsingGroup,
  beRecipientApiGetGroupDetails,
  beRecipientApiGetGroupsStats,
  beRecipientApiGetListOfGroupDetails,
  beRecipientApiUpdateGroup,
} from 'Api/beRecipientApiTS'

import { beListGetListsLazy } from 'Api/beListApiTS'
import { isNullOrEmpty } from 'Helpers/strings'
import type {
  GroupItem,
  GroupOperation,
  GroupStats,
  PaginationState,
  SearchState,
} from 'Pages/GroupsList/model'
import type { ListOut } from 'Types/beListApi'
import type {
  CopyGroupToListsRequest,
  EntityUsingItem,
  GenericTaskResponse,
  GetListOfGroupDetailsQsParams,
  GroupDetails,
  GroupIn,
} from 'Types/beRecipientsApi'

export const toGroupItem = ({
  Id,
  IdList,
  Name,
  Notes,
  Deleteable,
  Type,
}: GroupDetails): GroupItem => {
  return {
    Id,
    IdList,
    Name,
    Notes,
    Deleteable,
    Type,
  }
}

export const fetchResults = async (
  s: SearchState,
  p: Pick<
    PaginationState,
    'pageNumber' | 'pageSize' | 'orderBy' | 'orderDescending'
  >,
): Promise<{
  items: GroupItem[]
  pagination: Partial<PaginationState>
}> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST)

  const payload: Pick<
    GetListOfGroupDetailsQsParams,
    | 'pageSize'
    | 'orderBy'
    | 'orderDescending'
    | 'searchBy'
    | 'searchType'
    | 'groupType'
  > = {
    pageSize: p.pageSize,
    orderBy: p.orderBy,
    orderDescending: p.orderDescending,
    searchBy: s.searchBy,
    searchType: isNullOrEmpty(s.searchBy) ? 'CONTAINS' : undefined,
    groupType: s.groupType !== 'All' ? s.groupType : undefined,
  }

  const { Items, PageNumber, PageSize, TotalItems } =
    await beRecipientApiGetListOfGroupDetails(idList, {
      ...payload,
      pageNumber: p.pageNumber,
    })

  if (Items.length === 0 && TotalItems > 0) {
    //  wrong page
    const results2 = await beRecipientApiGetListOfGroupDetails(idList, {
      ...payload,
      pageNumber: Math.ceil(TotalItems / PageSize),
    })

    return {
      items: results2.Items.map(toGroupItem),
      pagination: {
        pageNumber: results2.PageNumber,
        pageSize: results2.PageSize,
        totalItems: results2.TotalItems,
      },
    }
  }

  return {
    items: Items.map(toGroupItem),
    pagination: {
      pageNumber: PageNumber,
      pageSize: PageSize,
      totalItems: TotalItems,
    },
  }
}

export const fetchGroup = async (
  idGroup: number,
): Promise<GroupDetails | null> => {
  const g = await beRecipientApiGetGroupDetails(idGroup)

  return g
}

export const fetchEntities = async (
  idGroup: number,
): Promise<EntityUsingItem[]> => {
  const result = await beRecipientApiGetEntitiesUsingGroup(idGroup)

  return result.sort((a, b) => a.Type.localeCompare(b.Type))
}

export const deleteOrEmptyGroups = async (
  ids: number[],
  op: GroupOperation,
): Promise<void> => {
  const idList = storeGet(STORE_KEY_ID_LIST)
  switch (op) {
    case 'DeleteGroup':
      await beRecipientApiDeleteGroups(ids)
      break

    case 'EmptyGroup':
      await beRecipientApiEmptyGroups(idList, {
        IdGroups: ids,
      })
      break

    case 'DeleteRecipients':
      await beRecipientApiDeleteGroupsRecipients(ids, true)
      break

    case 'DeleteGroupAndRecipients':
      await beRecipientApiDeleteGroupsRecipients(ids, false)
      break
  }
}

export const saveGroup = async (item: GroupItem): Promise<GroupItem> => {
  const IdList = storeGet(STORE_KEY_ID_LIST)

  const payload: GroupIn = {
    ...item,
    IdList,
    Deleteable: true,
  }

  const groupOut = await (item.Id === 0
    ? beRecipientApiAddGroup(payload)
    : beRecipientApiUpdateGroup(item.Id, payload))

  return groupOut
}

export const checkGroupConflict = async (item: GroupItem): Promise<boolean> => {
  const idList = storeGet(STORE_KEY_ID_LIST)

  const { Exists } = await beRecipientApiCheckIfGroupNameExists(idList, {
    Name: item.Name,
    IdGroupToExclude: item.Id,
  })

  return Exists
}

export const checkGroupNameExistsInList = async (
  Name: string,
  idList: number,
): Promise<boolean> => {
  const { Exists } = await beRecipientApiCheckIfGroupNameExists(idList, {
    Name,
    IdGroupToExclude: 0,
  })
  return Exists
}

export const getGroupsListStats = async (
  ids: number[],
): Promise<GroupStats[]> => {
  if (ids.length === 0) {
    return []
  }
  const result = await beRecipientApiGetGroupsStats(ids)

  return Array.isArray(result) ? result : []
}

export const getAllUserVisibileLists = async (): Promise<ListOut[]> => {
  const params = {
    force: true, // user-visible only
    pageSize: 0,
    orderBy: 'Id',
    orderDescending: false,
  }
  return (await beListGetListsLazy(params)).Items ?? []
}

export const copyGroupToLists = async (
  IdGroupSource: number,
  DestinationListIds: number[],
  DestinationGroupName: string,
): Promise<GenericTaskResponse> => {
  const data: CopyGroupToListsRequest = {
    IdGroupSource,
    DestinationListIds,
    DestinationGroupName,
  }
  return beRecipientApiCopyGroupToLists(data)
}
